'use client';

import { useEffect, useMemo } from 'react';

import { useOptionalFindSessionContext } from '@/app/(public)/FindSessionContext';
import {
  buildSiteNavZones,
  DEFAULT_SITE_NAVIGATION,
} from '@/components/layout/site-header/site-navigation-mappers';
import type { SiteNavZonesView } from '@/components/site-page/SitePage.types';
import { useParkingValidationVisible } from '@/hooks/parking-validation/useParkingValidationVisible';
import { useLoginHref } from '@/hooks/useLoginHref';
import { useSiteNavigationStore } from '@/stores/site-navigation.store';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';

const EMPTY_ZONES: SiteNavZonesView = { left: [], center: [], right: [] };

export interface UseSiteNavigationResult {
  /** True while the company has Site Builder on and the navigation has not answered yet. */
  isLoading: boolean;
  /** A published navigation is in force: the layouts render the site header. */
  isPublished: boolean;
  zones: SiteNavZonesView;
  showLogin: boolean;
  /** Raw FOOTER slots for `mapFooterSlots`; null when none. */
  footerSlots: Record<string, unknown> | null;
}

/**
 * Triggers the shared navigation read and resolves it for the current viewer.
 *
 * ## Invariants:
 * - No request unless `theme.siteBuilderEnabled === true`: every other company gets DEFAULT with no
 *   network traffic.
 * - `isPublished` is true only for a ready, PUBLISHED navigation. A failed read (`error`) is
 *   neither loading nor published, so the layouts fall back to today's header.
 * - Audience filtering and ACTION resolution are presentation only; every target keeps its guard.
 */
export function useSiteNavigation(): UseSiteNavigationResult {
  const theme = useThemeStore((s) => s.theme);
  const enabled = theme?.siteBuilderEnabled === true;
  const status = useSiteNavigationStore((s) => s.status);
  const navigation = useSiteNavigationStore((s) => s.navigation);
  const load = useSiteNavigationStore((s) => s.load);
  const isSignedIn = useAuthStore((s) => !!s.user);
  const validationVisible = useParkingValidationVisible();
  const loginHref = useLoginHref();
  const findSession = useOptionalFindSessionContext();
  const openFindSession = findSession?.openModal;

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  const published = enabled && status === 'ready' && navigation?.source === 'PUBLISHED';
  const model = published && navigation ? navigation : DEFAULT_SITE_NAVIGATION;

  const zones = useMemo(() => {
    if (!published) return EMPTY_ZONES;
    return buildSiteNavZones(model.nodes, {
      isSignedIn,
      theme,
      validationVisible,
      loginHref,
      openFindSession: openFindSession ? () => openFindSession() : null,
    });
  }, [published, model, isSignedIn, theme, validationVisible, loginHref, openFindSession]);

  return {
    isLoading: enabled && (status === 'idle' || status === 'loading'),
    isPublished: published,
    zones,
    showLogin: model.showLogin,
    footerSlots: published ? model.footerSlots : null,
  };
}
